import api from 'Cloud/Application/api';
import { extInfo } from 'lib/extInfo';
import {
    type FeedAnchorsApiV4ResponseItem,
    type FeedApiV4Response,
    type FeedApiV4ResponseListItem,
    FeedAnchorsApiCallV4,
    FeedApiCallV4,
} from 'reactApp/api/FeedAPICall';
import { galleryFeedV2ToV4 } from 'reactApp/appHelpers/featuresHelpers/features/galleryFastscroll';
import type { FeedApiResponse } from 'reactApp/modules/feed/feed.types';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { getItemNameWithoutExt, getParent, normalizeId, normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import type { GalleryAnchor, GalleryItem } from 'reactApp/modules/gallery/gallery.types';
import type { ESortOder } from 'reactApp/modules/sort/sort.types';
import { MalwareStatus } from 'reactApp/modules/start/start.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import { normalizeMtime } from 'reactApp/utils/tree.helpers';

type timestamp = number;

export interface GalleryAnchorRequest {
    extensions: string;
    include?: string[];
    exclude?: string[];
    timezone: string;
}

export const requestAnchorsList = async ({ extensions, include, exclude, timezone }: GalleryAnchorRequest) => {
    const { data } = await new FeedAnchorsApiCallV4().makeRequest({
        ignore_mounts: true,
        exts: extensions,
        include_paths: include,
        exclude_paths: exclude,
        timezone,
    });

    return data;
};

export const normalizeAnchorV4Item = ({ mtime_from, mtime_to, count }: FeedAnchorsApiV4ResponseItem): GalleryAnchor => {
    return {
        id: `${mtime_from}:${mtime_to}`,
        periodStart: mtime_from * 1000,
        periodEnd: mtime_to * 1000,
        count,
        isLoaded: false,
        isLoading: false,
        topIds: [],
        bottomIds: [],
        sideById: Object.create(null) as Record<string, 't' | 'b'>,
        cursorAsc: '',
        cursorDesc: '',
        loadingOrder: undefined,
    };
};

export interface GalleryListRequest {
    limit?: number;
    cursor?: string;
    extensions?: string;
    include?: string[];
    exclude?: string[];
    mtime_from?: timestamp;
    mtime_to?: timestamp;
    order?: ESortOder;
    ignore_mounts?: boolean;
}

export const requestGalleryList = async ({
    limit,
    cursor,
    extensions,
    include,
    exclude,
    mtime_from,
    mtime_to,
    order,
    ignore_mounts = false,
}: GalleryListRequest): Promise<{ galleryListV2?: FeedApiResponse; galleryListV4?: FeedApiV4Response }> => {
    let galleryListV2: FeedApiResponse | undefined;
    let galleryListV4: FeedApiV4Response | undefined;

    if (galleryFeedV2ToV4) {
        const response = await new FeedApiCallV4().makeRequest({
            exts: extensions,
            cursor,
            limit,
            include_paths: include,
            exclude_paths: exclude,
            ignore_mounts,
            mtime_from,
            mtime_to,
            order,
        });

        galleryListV4 = response.data;
    } else {
        galleryListV2 = await api.feed({
            exts: extensions,
            cursor,
            limit,
            include,
            exclude,
        });
    }

    return { galleryListV2, galleryListV4 };
};

const urlBuilder = new UrlBuilder();

export const normalizeFeedV4ItemToGallery = (item: FeedApiV4ResponseListItem): GalleryItem => {
    const homePath = item.path;
    const fileHash = item.hash || '';
    const parentFolder = homePath ? normalizeId(getParent(homePath)) : '/';
    const ext = getExtension(item);
    const nameWithoutExt = getItemNameWithoutExt(item.name, ext);
    const { kind, subKind } = extInfo.get(ext);

    const virusScan = item.malware?.status === MalwareStatus.infected ? 'fail' : item.virus_scan || 'pass';

    const normalizedItem: GalleryItem = {
        // isReadOnly false, потому что не запрашиваем монтированные папки
        isReadOnly: false,
        id: homePath,
        isFolder: false,
        parent: parentFolder,
        ext,
        nameWithoutExt,
        name: item.name,
        home: homePath || '',
        size: item.size,
        kind,
        subKind,
        storage: EStorageType.gallery,
        mode_bitmask: item.mode_bitmask,
        mtime: normalizeMtime(item.mtime),
        virusScan,
        hash: fileHash,
        // Хардкод, его нам не передают
        uflr: undefined,
        isInFavorites: !!item?.flags?.favorite,
        weblink: '',
        url: urlBuilder.getUrls({
            ext,
            isPublic: false,
            id: homePath,
            weblink: '',
            size: item.size,
            kind,
            subKind,
            isStock: false,
            name: item.name,
            path: homePath,
        }),
        thumbnails: urlBuilder.getThumb({
            hash: fileHash,
            ext,
            id: homePath,
            size: item.size,
            kind,
            name: item.name,
            path: homePath,
            isPublic: false,
            isStock: false,
            dwl_token: null,
            weblink: '',
        }),
    };

    if (item.public) {
        return {
            ...normalizedItem,
            ...normalizePublicApiInfo(item),
        };
    }

    return normalizedItem;
};
